import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { feeSchedulePrograms as fspUtils } from '@unite-us/client-utils';
import formatShortDate from 'common/utils/Dates/formatShortDate';
import getTimeDifference from 'common/utils/Dates/getTimeDifference';
import { DetailList } from './Layout';
import {
  AmountLineItem,
  BaseLineItem,
  ClinicalModificationsLineItem,
  ServiceDeliveryDatesLineItem,
} from './LineItems';

const SubmittedByLineItem = ({ authorization }) => {
  const isLoaded = !isEmpty(authorization.requester) && !isEmpty(authorization.requester.provider);

  return (
    <BaseLineItem
      field="Submitted By"
      dataTestElement="submitted-by"
      isLoaded={isLoaded}
    >
      {authorization.requester?.full_name}
      <br />
      ({authorization.requester?.provider?.name})
    </BaseLineItem>
  );
};

const RequestDateLineItem = ({ authorization, dateRequested }) => {
  const isLoaded = !!dateRequested || authorization?.auto_approved;
  const value = authorization?.auto_approved ?
    formatShortDate(authorization.updated_at) :
    formatShortDate(dateRequested);

  return (
    <BaseLineItem
      field="Date of Request"
      isLoaded={isLoaded}
      dataTestElement="request-date"
    >
      {value}
    </BaseLineItem>
  );
};

const TimeInQueueLineItem = ({ authorization }) => {
  const shouldShow = authorization.state === 'requested';

  if (!shouldShow) return null;

  return (
    <BaseLineItem field="Time in Queue" dataTestElement="time-in-queue">
      {getTimeDifference(authorization.updated_at)}
    </BaseLineItem>
  );
};

const AuthorizationSummarySection = ({
  authorization,
  feeScheduleProgram,
  dateRequested,
  previousAuth,
  showOriginalValues,
}) => (
  <DetailList>
    <AmountLineItem
      currentAmount={authorization.requested_unit_amount || authorization.requested_cents}
      fieldName="Amount Requested"
      isUnitBased={!fspUtils.isCostPaymentType(feeScheduleProgram.payment_type)}
      previousAmount={previousAuth.requested_unit_amount || previousAuth.requested_cents}
      showOriginalValues={showOriginalValues}
      unit={feeScheduleProgram.unit}
    />
    <ServiceDeliveryDatesLineItem
      endDate={authorization.requested_ends_at}
      fieldName="Service Delivery Date(s) Requested"
      previousEndDate={previousAuth.requested_ends_at}
      previousStartDate={previousAuth.requested_starts_at}
      showOriginalValues={showOriginalValues}
      startDate={authorization.requested_starts_at}
      testElement="service-delivery-dates"
    />
    {feeScheduleProgram.cm_code_required_on_authorization && (
      <ClinicalModificationsLineItem
        clinicalModifications={authorization.clinical_modifications}
        previousClinicalModifications={previousAuth.clinical_modifications}
        showOriginalValues={showOriginalValues}
      />
    )}
    <SubmittedByLineItem authorization={authorization} />
    <RequestDateLineItem
      authorization={authorization}
      dateRequested={dateRequested}
    />
    <TimeInQueueLineItem authorization={authorization} />
    {authorization.update_request_note && (
      <BaseLineItem field="Request Note" dataTestElement="update-request-note">
        {authorization.update_request_note}
      </BaseLineItem>
    )}
  </DetailList>
);

SubmittedByLineItem.propTypes = {
  authorization: PropTypes.shape({
    requester: PropTypes.shape({
      full_name: PropTypes.string,
      provider: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
};

RequestDateLineItem.propTypes = {
  authorization: PropTypes.shape({
    auto_approved: PropTypes.bool,
    updated_at: PropTypes.string,
  }).isRequired,
  dateRequested: PropTypes.string,
};

RequestDateLineItem.defaultProps = {
  dateRequested: null,
};

TimeInQueueLineItem.propTypes = {
  authorization: PropTypes.shape({
    state: PropTypes.string.isRequired,
    updated_at: PropTypes.string.isRequired,
  }).isRequired,
};

AuthorizationSummarySection.propTypes = {
  authorization: PropTypes.shape({
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
    requested_starts_at: PropTypes.string.isRequired,
    requested_ends_at: PropTypes.string.isRequired,
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
    requester: PropTypes.shape({
      full_name: PropTypes.string,
      provider: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    auto_approved: PropTypes.bool,
    updated_at: PropTypes.string,
    state: PropTypes.string.isRequired,
    update_request_note: PropTypes.string,
  }).isRequired,
  feeScheduleProgram: PropTypes.shape({
    cm_code_required_on_authorization: PropTypes.bool,
    payment_type: PropTypes.string.isRequired,
    unit: PropTypes.string,
  }).isRequired,
  dateRequested: PropTypes.string,
  previousAuth: PropTypes.shape({
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
    requested_starts_at: PropTypes.string,
    requested_ends_at: PropTypes.string,
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
  }),
  showOriginalValues: PropTypes.bool,
};

AuthorizationSummarySection.defaultProps = {
  dateRequested: null,
  previousAuth: {},
  showOriginalValues: false,
};

export default AuthorizationSummarySection;
